<script setup lang="ts">
import { GLOBAL_SHORTCUTS, useShortcut } from '@erpx/ui'
import { computed, nextTick, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterView, useRoute } from 'vue-router'

import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import AppSidebar from '@/components/AppSidebar.vue'
import AppTopBar from '@/components/AppTopBar.vue'
import CommandPaletteHost from '@/components/CommandPaletteHost.vue'
import RightRail from '@/components/RightRail.vue'
import ShortcutCheatSheet from '@/components/ShortcutCheatSheet.vue'
import TenantBanner from '@/components/TenantBanner.vue'
import { NAV_TOGGLE_ID, useCurrentApp, useNavPrefs } from '@/composables/useNavPrefs'
import { useOverlays } from '@/composables/useOverlays'
import { QUICK_CREATE_ACTIONS, useQuickCreate } from '@/composables/useQuickCreate'
import { useReadOnly } from '@/composables/useReadOnly'
import { useRightRail } from '@/composables/useRightRail'
import { useUserScope } from '@/composables/useUserScope'
import InviteUserDrawer from '@/modules/users/InviteUserDrawer.vue'
import { itemForPath } from '@/nav/items'
import { useAuthStore } from '@/stores/auth'

/**
 * The application shell: command bar across the top, then the current app's
 * sidebar, the workspace and the right rail.
 *
 * The top bar spans the full width rather than sitting beside the sidebar so
 * that it never moves: on `/` (the home screen, a launcher) there is no
 * sidebar at all, and a top bar that jumped sideways every time the user went
 * home and back would make the brand mark, the launcher and search a moving
 * target. Only the workspace changes width.
 *
 * It also owns the global keyboard map. Registering the bindings here rather
 * than in each screen means the `?` cheat sheet lists them everywhere, and a
 * screen cannot accidentally shadow ⌘K. (The launcher's own binding lives in
 * `AppLauncher.vue`, beside the open state it toggles; it is mounted for
 * exactly as long as the shell is.)
 */

const auth = useAuthStore()
const route = useRoute()
const { t } = useI18n()
const overlays = useOverlays()
const rail = useRightRail()
const quickCreate = useQuickCreate()
const { readOnly } = useReadOnly()

const userScope = useUserScope()
const prefs = useNavPrefs(userScope, auth.can)
const { current, onHomeScreen } = useCurrentApp(userScope, auth.can)

/**
 * Recents and the last-used app follow real navigation, not clicks, so a deep
 * link or a Back button updates them exactly as a sidebar click does. They
 * are recorded here rather than in the sidebar because the home screen shows
 * recents and has no sidebar: a watcher in a component that `/` never mounts
 * would stop recording the moment the user went home.
 *
 * The home screen itself is never recorded. It is where Recent is shown (and
 * it filters `/` out), so recording it only spent one of the six slots on a
 * screen that is never displayed — every trip home evicted a real one.
 */
watch(
  () => route.path,
  (path) => {
    const item = itemForPath(path)
    if (item && item.to !== '/') prefs.noteVisit(item.id)
    // Following a link from the drawer must not leave it covering the page.
    prefs.drawerOpen.value = false
  },
  { immediate: true },
)

watch(
  () => current.value?.app.id,
  (id) => {
    if (id !== undefined) prefs.setLastApp(id)
  },
  { immediate: true },
)

function closeDrawer(): void {
  if (!prefs.drawerOpen.value) return
  prefs.drawerOpen.value = false
  // Back to the control that opened it (WCAG 2.4.3), not to <body>.
  void nextTick(() => document.getElementById(NAV_TOGGLE_ID)?.focus())
}

/**
 * XFN-UX-011's two shell-level slices: a skip link (below, in the template)
 * and this — focus reset plus a live-region announcement on route change.
 *
 * Before this, a screen reader user who followed a sidebar link kept hearing
 * the sidebar: nothing moved focus into the page that had just loaded, and
 * nothing said its name. Two things happen on every in-app navigation:
 *
 *  - **Focus moves to the main landmark.** `mainEl` carries `tabindex="-1"`
 *    so it is a valid focus target without joining the tab order — the same
 *    technique used for the skip link's own destination.
 *  - **The new page's name is announced** through a visually-hidden
 *    `aria-live="polite"` region, using the same `titleKey` the router
 *    already resolves for `document.title` (`router/index.ts`'s
 *    `afterEach`), so the two never say different things.
 *
 * Not run on the very first load: a cold page load already gets the
 * document title read by the screen reader's own "page loaded"
 * announcement, and yanking focus off the URL bar on first paint would be
 * surprising rather than helpful. `watch` without `immediate` is exactly
 * "on every route CHANGE".
 */
const mainEl = ref<HTMLElement | null>(null)
const routeAnnouncement = ref('')

watch(
  () => route.fullPath,
  () => {
    const key = route.meta.titleKey
    routeAnnouncement.value = key === undefined ? t('app.name') : t(key)
    void nextTick(() => mainEl.value?.focus())
  },
)

const firstCreate = computed(() =>
  QUICK_CREATE_ACTIONS.find((action) => auth.can(action.permission)),
)

useShortcut(
  GLOBAL_SHORTCUTS.commandPalette,
  () => overlays.openPalette(),
  {
    // Reachable while typing: the palette is how you leave wherever you are.
    allowInInput: true,
    describe: { id: 'global-palette', description: 'shell.shortcuts.palette', group: 'global' },
  },
)

useShortcut(GLOBAL_SHORTCUTS.search, () => overlays.openPalette(), {
  allowInInput: true,
  describe: { id: 'global-search', description: 'shell.shortcuts.search', group: 'global' },
})

useShortcut(GLOBAL_SHORTCUTS.cheatSheet, () => overlays.openCheatSheet(), {
  describe: { id: 'global-cheatsheet', description: 'shell.shortcuts.cheatSheet', group: 'global' },
})

useShortcut(
  GLOBAL_SHORTCUTS.quickCreate,
  () => {
    const action = firstCreate.value
    if (action && !readOnly.value) quickCreate.open(action.id)
  },
  {
    allowInInput: true,
    describe: { id: 'global-quick-create', description: 'shell.shortcuts.quickCreate', group: 'global' },
  },
)

useShortcut('mod+shift+i', () => rail.toggle(), {
  allowInInput: true,
  describe: { id: 'global-insights', description: 'shell.shortcuts.insights', group: 'global' },
})

// Not advertised in the cheat sheet: Escape closing whatever is open is
// expected, not a shortcut to learn. Inert unless the narrow-screen drawer is
// actually open, so it never competes with a dialog's own Escape.
useShortcut(GLOBAL_SHORTCUTS.closeOverlay, closeDrawer, {
  allowInInput: true,
  enabled: prefs.drawerOpen,
})
</script>

<template>
  <div class="shell" :class="{ 'shell--home': onHomeScreen }">
    <a class="a-skip-link" href="#web-main">{{ t('app.skipToContent') }}</a>

    <TenantBanner />

    <AppTopBar />

    <div class="shell__body">
      <template v-if="!onHomeScreen">
        <!-- Narrow screens only (styles.css): the drawer's backdrop. A pointer
             affordance; keyboard users close it with Escape. -->
        <div
          v-if="prefs.drawerOpen.value"
          class="shell__scrim"
          aria-hidden="true"
          @click="closeDrawer"
        />
        <AppSidebar />
      </template>

      <main id="web-main" ref="mainEl" class="shell__workspace" tabindex="-1">
        <AppBreadcrumbs />
        <RouterView />
      </main>

      <RightRail />
    </div>

    <p class="sr-only" role="status" aria-live="polite">{{ routeAnnouncement }}</p>

    <CommandPaletteHost />
    <ShortcutCheatSheet />
    <InviteUserDrawer
      :open="quickCreate.isOpen('invite-user')"
      @update:open="(value: boolean) => (value ? quickCreate.open('invite-user') : quickCreate.close())"
    />
  </div>
</template>
